// Mesas del salón que toma una jornada del club ("actividad de salón") — capa de API.
// Molde: `membershipsApi.js`; el wrapper HTTP vive en `apiClient.js`.
//
// DOS formas distintas de `activity`, y confundirlas deja la pantalla en blanco:
//
//   - La que se PINTA es el objeto plano de `getById`: `{ id, name, status, date, startHour,
//     hasStarted, tableCount, expected, redeemed, plannedTables[], reservations[],
//     gamesByTable }`, todo al tope. Llega por `getByEvent` / `getActivity`, y es el mismo que
//     viaja como `event.activity` en `GET /manage-events/:id`.
//   - La que devuelven las MUTACIONES (`createActivity`, `addTable`, `removeTable`,
//     `startActivity`, `releaseAll`) es la fila del modelo Sequelize, o sea snake_case
//     (`start_hour`, `event_id`, `seated_at`…) y SIN `tableCount`, `hasStarted`, `expected`,
//     `redeemed`, `plannedTables`, `reservations` ni `gamesByTable`.
//
// Por eso la regla de la card es: de una mutación se leen `warnings` / `released` / `kept` /
// `seated` y nada más, y el estado se vuelve a pedir (`onRefresh()` o `getByEvent`). Pintar con
// lo que devuelve `addTable` deja la card vacía después de cada acción, sin ningún error
// visible.
//
// Errores: el bloque 73xx. Los textos de 7300/7301/7303/7306/7309 los fija `errors_enum` y
// pisan el del throw; 7304 ('Mesas no disponibles') trae la lista de mesas ocupadas en
// `err.body.data` (un ARRAY `[{ label, reason }]`, no un objeto con `.message`), y 7305, 7307,
// 7308 y 7310 llegan con su frase completa en `err.message`.

// `del` SÍ existe en `apiClient` (`export const del = (path) => request(path, { method:
// 'DELETE' })`): no hace falta escribir el DELETE a mano sobre `request`.
import { get, post, patch, del } from './apiClient';

// GET /manage-actividades?eventId= — la actividad de la jornada, o `{ activity: null }` con
// error 0 cuando todavía no tomó mesas (que es el estado normal de casi todos los eventos).
//
// El guard del `eventId` es del cliente a propósito: el backend rechaza con `AppError(1001)`,
// y como el 1001 está declarado en el enum su mensaje propio ("Indicá de qué jornada querés
// ver las mesas") se pierde y el usuario lee "Request inválida".
export async function getByEvent(eventId) {
    if (eventId == null || eventId === '') throw new Error('Indicá de qué jornada querés ver las mesas.');
    return get(`manage-actividades?eventId=${encodeURIComponent(eventId)}`);
}

// GET /manage-actividades/:id — el mismo objeto plano, por id de actividad.
export const getActivity = (id) => get(`manage-actividades/${id}`);

// GET /manage-actividades/availability?date=&startHour= — qué mesas se pueden sumar al slot y
// el motivo de cada una que no: `{ businessDate, fromHour, closedDay, tables:[{ id, label,
// capacity, isVip, state:'free'|'busy'|'blocked', reason?, nextReservation? }] }`.
//
// `date` y `startHour` son OPCIONALES: sin ellos el backend responde por la jornada del salón
// de hoy y devuelve su `businessDate`. Es la única fuente de esa fecha — el navegador no la
// deriva (TC-002).
//
// `startsAt` es la fecha-hora CRUDA de un formulario ("2026-10-20T20:00"), para cuando todavía
// no hay ni evento ni actividad (el alta de un evento privado). Viaja SOLA: de qué jornada es esa
// hora —un evento de las 00:30 es de la jornada de ayer— lo deriva el servidor, y mandarle además
// un `date`/`startHour` armado acá sería decirle dos cosas. La respuesta suma entonces si el
// salón abre a esa hora; se lee con `aperturaDelSalon`.
export const availability = ({ date, startHour, startsAt } = {}) => {
    const params = new URLSearchParams();
    if (startsAt) {
        params.set('startsAt', startsAt);
    } else {
        if (date) params.set('date', date);
        if (startHour !== undefined && startHour !== null && startHour !== '') params.set('startHour', startHour);
    }
    const qs = params.toString();
    return get(`manage-actividades/availability${qs ? `?${qs}` : ''}`);
};

// Si el salón abre a la hora que se consultó con `startsAt`, y si no, el motivo (las mismas
// palabras que el rechazo 7301). Es el ÚNICO lugar que conoce las dos claves de la respuesta:
// `open` (false = no abre) y `closedReason`. Sin la clave —una consulta por `date`/`startHour`, o
// un backend que todavía no la manda— se toma por abierto: el que decide es el servidor, que al
// crear rechaza igual con 7301.
export const aperturaDelSalon = (disponibilidad) => {
    const abre = !(disponibilidad && disponibilidad.open === false);
    return {
        open: abre,
        reason: abre ? null : ((disponibilidad && disponibilidad.closedReason) || 'El salón no abre a esa hora'),
    };
};

// POST /manage-actividades — la PRIMERA mesa de la jornada: crea la actividad y, si el evento
// ya está publicado, materializa las hijas en el acto. `capacityDelta` sube el cupo del evento
// en la MISMA transacción ("Sumar mesa y ampliar cupo").
export const createActivity = ({ eventId, tableIds, capacityDelta } = {}) =>
    post('manage-actividades', {
        eventId,
        tableIds,
        ...(capacityDelta ? { capacityDelta } : {}),
    });

// POST /manage-actividades/:id/tables — las mesas SIGUIENTES. Mismo botón que `createActivity`
// del lado de la pantalla, así que devuelve la misma forma.
export async function addTable(id, { tableId, capacityDelta } = {}) {
    // Igual que arriba: el 'Elegí la mesa que se suma a la jornada' del backend viaja en un
    // 1001 declarado y no llega. Se valida acá para que el texto sea útil.
    if (tableId == null || tableId === '') throw new Error('Elegí la mesa que se suma a la jornada.');
    return post(`manage-actividades/${id}/tables`, {
        tableId,
        ...(capacityDelta ? { capacityDelta } : {}),
    });
}

// DELETE /manage-actividades/:id/tables/:tableId — quitar una mesa del plan (7305 si hay gente
// sentada o consumo impago).
export async function removeTable(id, tableId) {
    if (tableId == null || tableId === '') throw new Error('Indicá qué mesa se quita.');
    return del(`manage-actividades/${id}/tables/${tableId}`);
}

// PATCH /manage-actividades/:id/start — "Sentar las mesas" / "Sentar la jornada".
//
// ⚠️ OMITIR `headcount` NO es lo mismo que mandar 0: omitido reparte los `redeemed` del evento
// entre las mesas (lo que quiere la card de la jornada), y `0` explícito es "sólo sentar" (lo
// que quiere el panel del salón, que ya sumó +1 por cada entrada en el mostrador). Por eso las
// claves se agregan sólo si vinieron.
//
// `everyone: true` es "Sentar a todos": sienta todas las mesas, reparte parejo entre ellas a los
// anotados del evento y marca sus entradas como ingresadas. Viaja SOLO: el backend rechaza la
// combinación con `headcount` o `tableId` (1001).
export const startActivity = (id, { headcount, tableId, everyone } = {}) =>
    patch(`manage-actividades/${id}/start`, everyone ? { everyone: true } : {
        ...(headcount === undefined || headcount === null || headcount === '' ? {} : { headcount: Number(headcount) }),
        ...(tableId === undefined || tableId === null || tableId === '' ? {} : { tableId }),
    });

// PATCH /manage-actividades/:id/release-all — "Liberar todas" al cierre de la noche. Devuelve
// `{ activity, released[], kept[], closed }`: `kept` son las mesas que NO se liberaron porque
// tienen consumo impago, con su `balance` — hay que mostrarlas, si no el encargado cree que
// cerró la noche y quedan dos mesas con la cuenta abierta.
export const releaseAll = (id) => patch(`manage-actividades/${id}/release-all`, {});
// La mesa de cada persona ANTES de la noche: sortear (o volver a sortear) y reasignar a mano.
export const drawTables = (id) => patch(`manage-actividades/${id}/draw`, {});
export const assignTable = (id, admissionId, tableId) =>
    patch(`manage-actividades/${id}/assign`, { admissionId, tableId: tableId == null || tableId === '' ? null : Number(tableId) });

// ── vocabulario de UI ──

export const ESTADO_ACTIVIDAD = {
    planned:   { label: 'Planificada', badge: 'badge-light' },
    reserved:  { label: 'Mesas reservadas', badge: 'badge-success' },
    seated:    { label: 'En mesa', badge: 'badge-info' },
    done:      { label: 'Terminada', badge: 'badge-secondary' },
    cancelled: { label: 'Cancelada', badge: 'badge-danger' },
};

// Motivo por el que una mesa no se puede sumar, tal como lo pinta el select de "Sumar mesa".
export const MOTIVO_MESA = {
    busy:    'Ocupada',
    blocked: 'Bloqueada',
};

// Un aviso del salón es SIEMPRE un objeto `{ label, reason }` — lo arma
// `ActivityController.lockAndValidateTables` ("6-C" + "tiene una reserva a las 21") y lo
// reenvían tal cual las respuestas que traen `warnings`: las mutaciones de
// `/manage-actividades` y el `PATCH /manage-events/:id` que publica, reprograma o cierra una
// jornada. NUNCA es un string, así que un `warnings.join()` imprime "[object Object]" en
// pantalla sin que nada falle ni quede en consola.
//
// Vive acá, y no copiado en cada pantalla, porque son CINCO los consumidores (la card de la
// jornada, la edición del evento, el aviso del check-in, el panel del salón y la serie) y ya
// pasó una vez que el quinto se desviara.
export const textoDeAviso = (w) => {
    if (w && w.label) return `${w.label}: ${w.reason}`;
    return (w && w.reason) || String(w);
};

// La misma lista en una línea, para los avisos que van dentro de una frase y no como `<li>`.
// Con el array vacío (o con algo que no sea un array) devuelve ''.
export const textoDeAvisos = (warnings) =>
    (Array.isArray(warnings) ? warnings : []).map(textoDeAviso).join(' · ');

// El 7304 no trae el motivo en `data.message` (su `data` es la LISTA), así que `apiClient` cae
// al message genérico y las mesas quedan en `err.body.data`. Esto las saca.
export const mesasOcupadasDelError = (error) => {
    const data = error && error.body && error.body.data;
    return Array.isArray(data) ? data : [];
};

// Re-export: el nombre del módulo no cambia la regla de formateo de fechas.
export { formatYMD } from './formats';
